/**
 * admin.js — 後台（admin / auth）JS entry
 *
 * 與前台 main.js 分離：不載 hero / three / render-cards / site-nav 等前台腳本，
 * 只載後台外殼與表單元件。每個 admin/auth HTML 於 <head> 自帶
 * <script type="module" src="/src/admin.js">（不依賴 vite.config 的全域 entry 注入，
 * 該注入硬寫 /src/main.js）。
 *
 * CSS 同樣不在此 import：由 admin/auth HTML 自帶
 * <link rel="stylesheet" href="/src/styles/pages/admin.css">（render-blocking，
 * admin.css 內 @import main.css 繼承全套 token）。此處不 import CSS 避免 dev 雙載 /
 * build 重複輸出（對齊 main.js 慣例）。
 *
 * 載入即 auto-init 的元件（各自模組底部有 DOMContentLoaded / 立即 bind）：
 *   - admin-topnav.js：深藍頂部 NAV — 漢堡行動面板 + 身分 dropdown + platform 分組 dropdown
 *   - modal.js：[data-modal-trigger] / [data-modal-dismiss]
 *   - admin-table.js：[data-admin-table] 排序 / 分頁 / 篩選 / 拖曳重排（無對應 DOM 即 no-op）
 *   - admin-editor.js：[data-editor] 區塊式視覺編輯器（article-edit 用，無對應 DOM 即 no-op）
 *   - admin-toast.js：掛 window.AdminToast.show（容器 lazy 建立，無 DOM 副作用）
 *   - cr-form-card.js：[data-cr-form] 多 step 表單（clinic-register 用）
 */

// 禁用瀏覽器 reload 自動還原 scroll（與 main.js 一致，避免後台長表單跳位）
if ('scrollRestoration' in history) {
  history.scrollRestoration = 'manual';
}

import './scripts/components/admin-topnav.js';
import './scripts/components/modal.js';
// AI 助理 widget（比照前台 main.js）：ai-fab.js 控顯隱、chat-dialog.js 控 mock 對話。
// 後台無 .hero 且 dashboard / 短表單頁常捲不到前台的 scrollY>300 閾值，故 ai-fab.js 對
// body.page-admin / .page-member 走「進頁即恆顯」分支（見 ai-fab.js case 3），不綁捲動。
// markup 由各 admin/member 頁的 <%- include('_ai-chat.html') %> 注入；CSS（ai-fab.css /
// chat-dialog.css）已隨 admin.css @import main.css 載入。兩支模組底部各自 querySelector
// 守門早退，無 .ai-fab / #chat-dialog DOM 即 no-op。
import './scripts/ai-fab.js';
import './scripts/chat-dialog.js';
import './scripts/components/admin-table.js';
import './scripts/components/admin-editor.js';
import './scripts/components/admin-toast.js';
// cr-form-card.js（~39KB）只有帶 [data-cr-form] 的頁面用到（後台目前無、auth/clinic-register 一頁）。
// 頁面守門 dynamic import——有 form 才下載該 chunk，其餘 admin/auth/member 頁不再背 39KB。
// 模組底部自帶 auto-init（import 即 bindAll），import() 完成即接線。
if (document.querySelector('[data-cr-form]')) {
  import('./scripts/components/cr-form-card.js');
}

// clinic-edit 三段式營業時間（方向 A：動態增減時段）：每天 1→3 段、複製上一天 /
// 套用平日·全部、公休/預約制聯動、提交驗證 + 雙 DOM 去重。auto-init，
// 無 .admin-clinic-form[data-hours] 即 no-op（不影響其他 admin 頁）。
import './scripts/components/clinic-hours.js';

// Pilot ① doctors 新增 Modal 欄位（field-factory 首次實戰）：以 formField() 執行期產出 modal 內
// 文字/選單/textarea 欄位 + data-rules 規則宣告。**必須在 admin-form-edit.js 之前** import：
// 確保欄位先掛載，引擎 init() 才掃得到已含 data-rules 的完整欄位（見模組檔頭載入時序說明）。
// 此 import 同時把 field-factory.js 帶入後台 bundle（階段 0 未活化 factory，於此首次活化）。
// 無 [data-doctor-modal-fields]（其他頁）即 no-op。
import './scripts/pages/doctors-modal-form.js';

// doctors 醫師管理：狀態 tabs（全部 / 已通過 / 送審中 / 需修改）過濾卡片網格 + 關鍵字即時篩 +
// 0 筆空態切換。複用 list-filter.js 引擎（tabs 選擇器 + data-filter-value 經 config 注入）。
// auto-init，無 [data-doctor-grid-filter] / #doctor-grid（其他頁）即 no-op。
import './scripts/pages/admin-doctor-filter.js';

// 表單編輯頁欄位驗證 + 提交流程框架（clinic/doctor/article/product edit + doctors 新增 modal）：
// 欄位即時驗證 + 提交 spinner/toast + 圖片上傳 UX + dirty 離開攔截 + 鎖定欄 hint。submit 掛
// bubble，與 clinic-hours.js 的 capture submit 驗證共存（hours 錯先擋、hours 對才驗一般欄位）。
// auto-init，無對應 form DOM 即 no-op。
import './scripts/components/admin-form-edit.js';

// Pilot ② platform-privacy 隱私政策編輯頁「發布／草稿意圖」接線（修黑洞②）：按「確定發布」→
// 設 publishStatus=發布 + 關發布 modal（讓驗證失敗的 summary/inline 可見）；按「儲存草稿」→
// 設草稿 + 清 dirty + toast。條件式必填（版本摘要 / 政策內文）在發布時生效、草稿時放行的關鍵接點。
// 載於 admin-form-edit.js 之後（頁面語義層疊在引擎上）。auto-init，無 #privacy-edit-form 即 no-op。
import './scripts/pages/privacy-edit.js';

// clinic-edit 專長項目「其他（請說明）」條件式揭露：勾→揭露自填 input + 啟用 + focus；
// 取消→收合 + disable + 清值。與 admin-form-edit 驗證共存（收合時 disabled，不誤驗）。
// auto-init，無 input[name="specialty-other-toggle"] 即 no-op。
import './scripts/components/specialty-other.js';

// 詳情審核頁決策接線（consult / platform-*-review-detail）：退回必填驗證 / 成功 toast /
// 狀態 badge 翻轉 / 動作鈕終態 / consult 回覆插入。auto-init，無對應 DOM 即 no-op。
import './scripts/components/admin-review-detail.js';

// 後台進場動畫完成後釋放 transform 鎖：監聽 adminReveal animationend 加 .reveal-done，
// 卸掉 fill:both 鎖住的 transform，使可點 stat 卡 :hover translateY(-1px) 生效。
// auto-init，無 .page-admin/.page-auth/.page-member 殼或無 [data-reveal] 即 no-op；PRM 下不觸發。
import './scripts/components/admin-reveal-release.js';

// （2026-06-18 方向A chunk4 巧思②）統計卡數字 count-up 進場：.stat-card__value 0→目標值（ease-out）
// + 數字本體 fade-up，框不淡入。自帶獨立 IO（解耦 adminReveal/scroll-reveal），auto-init，
// 無 .stat-card__value 即 no-op；PRM/no-JS 顯終值不計數。
import './scripts/components/stat-count-up.js';

// 會員中心（/member/*）互動：各模組底部 auto-init，無對應 DOM 即 no-op（不影響 admin/auth 頁）。
import './scripts/components/member-favorites-tab.js';
import './scripts/components/member-replies.js';
import './scripts/components/member-edit.js';
// 頭像本機預覽（F2）：選圖→FileReader→替換頭像 + 隱字首 fallback，純前端 demo 不傳後端。
// 底部 auto-init，無 [data-member-avatar] 即 no-op。
import './scripts/components/member-avatar.js';

// auth（/auth/*）互動：register 雙 tab + 同意 gating + 密碼即時驗證、forgot/reset submit。
// 模組底部 auto-init，無對應 DOM（[data-auth-register] / [data-auth-pwd-form]）即 no-op。
import './scripts/components/auth-forms.js';

// /auth/login demo 行為：雙 tab 切換 + 按登入直通後台（讀 data-login-target，不驗帳密）。
// root 守門早退（無 #login-form 即 no-op）。login 頁 entry 是 admin.js（非 main.js），故掛此。
import './scripts/pages/login-demo.js';

// （2026-06-09）auth 8 頁 nav 改掛前台共用 _nav.html（.site-nav--solid-navy）→ 需 site-nav.js
// 驅動手機漢堡 drawer 開合（.nav-toggle 點擊 → .site-nav--menu-open + backdrop + aria-expanded）。
// auth 頁 entry 是 admin.js（非 main.js，main.js 才有此 import），故於此補掛。site-nav.js 底部
// auto-init 並守門早退（無 .site-nav / .nav-toggle 即 no-op），對其他 admin/member 頁無副作用。
import './scripts/site-nav.js';

// （2026-06-09）login 極光背景已退場（淺底 ink-on-warm-paper 改版，SPEC §9.2）：
//   原 login-aurora.js + components/aurora.js（ogl GLSL）已刪除，import 移除。
